<script setup lang="ts">
import { PhAndroidLogo, PhBatteryCharging, PhCloud, PhDeviceMobile, PhHouseLine, PhTrash } from "@phosphor-icons/vue"
import type { TableColumn, TableRow } from "@nuxt/ui"
import { computed, reactive, ref } from "vue"
import type { Component } from "vue"
import CapacityMeter from "../components/CapacityMeter.vue"
import ClippedValue from "../components/ClippedValue.vue"
import CopyButton from "../components/CopyButton.vue"
import CountryFlag from "../components/CountryFlag.vue"
import DataState from "../components/DataState.vue"
import DetailPanel from "../components/DetailPanel.vue"
import FilterBar from "../components/FilterBar.vue"
import FilterSearch from "../components/FilterSearch.vue"
import FilterSelect from "../components/FilterSelect.vue"
import MetricTile from "../components/MetricTile.vue"
import NullBadge from "../components/NullBadge.vue"
import PageHeader from "../components/PageHeader.vue"
import SortableTable from "../components/SortableTable.vue"
import StatusBadge from "../components/StatusBadge.vue"
import { useDashboardApi } from "../composables/useDashboardApi"
import DashboardLayout from "../layouts/DashboardLayout.vue"
import { useAuthStore } from "../stores/auth"
import { useDashboardStore } from "../stores/dashboard"
import type { ConnectedNode, NodeCategory, NodeStatus } from "../utils/dashboard-types"
import { formatNumber, formatRelative } from "../utils/format"

type TableSorting = Array<{ id: string; desc: boolean }>
type NodeFilters = {
  category: "all" | NodeCategory
  charging: "all" | "charging" | "not-charging"
  country: string
  search: string
  status: "all" | NodeStatus
  transport: "all" | NonNullable<ConnectedNode["transport"]>
}

const dashboard = useDashboardStore()
const auth = useAuthStore()
const selectedNodeId = ref("")
const nodeSorting = ref<TableSorting>([{ id: "lastSeenAt", desc: true }])
const clearOfflineRunning = ref(false)
const clearOfflineMessage = ref("")
const clearOfflineError = ref(false)
const defaultFilters: NodeFilters = {
  category: "all",
  charging: "all",
  country: "all",
  search: "",
  status: "all",
  transport: "all"
}
const filters = reactive<NodeFilters>({ ...defaultFilters })

const statusItems = [
  { label: "All statuses", value: "all" },
  { label: "Available", value: "available" },
  { label: "Draining", value: "draining" },
  { label: "Stale", value: "stale" },
  { label: "Probationary", value: "probationary" },
  { label: "Blocked", value: "blocked" },
  { label: "Offline", value: "offline" }
]
const categoryItems = [
  { label: "All categories", value: "all" },
  { label: "Android SDK", value: "android-sdk" },
  { label: "Residential ISP", value: "residential-isp" },
  { label: "VPS", value: "vps" }
]
const countryItems = computed(() => [
  { label: "All countries", value: "all" },
  ...Array.from(
    new Set(dashboard.nodes.map((node) => node.country).filter((country): country is string => Boolean(country)))
  )
    .sort()
    .map((country) => ({ label: country, value: country }))
])
const chargingItems = [
  { label: "Charging state", value: "all" },
  { label: "Charging", value: "charging" },
  { label: "Not charging", value: "not-charging" }
]
const transportItems = [
  { label: "All transports", value: "all" },
  { label: "Relay MASQUE", value: "relay-masque" }
]
const nodeCategoryIcons = {
  "android-sdk": PhDeviceMobile,
  "residential-isp": PhHouseLine,
  vps: PhCloud
} satisfies Record<NodeCategory, Component>
const filteredNodes = computed(() => {
  const search = filters.search.trim().toLowerCase()

  return dashboard.nodes.filter((node) => {
    const matchesSearch =
      !search ||
      [
        node.id,
        node.deviceId,
        node.observedIpAddress,
        node.country,
        node.city,
        node.regionCode,
        node.timezone,
        node.asn,
        node.isp,
        node.relayRegion,
        node.nodeCategory
      ]
        .join(" ")
        .toLowerCase()
        .includes(search)
    const matchesStatus = filters.status === "all" || node.status === filters.status
    const matchesCountry = filters.country === "all" || node.country === filters.country
    const matchesCategory = filters.category === "all" || node.nodeCategory === filters.category
    const matchesCharging =
      filters.charging === "all" ||
      (filters.charging === "charging" && node.charging) ||
      (filters.charging === "not-charging" && node.charging === false)
    const matchesTransport = filters.transport === "all" || node.transport === filters.transport

    return matchesSearch && matchesStatus && matchesCountry && matchesCategory && matchesCharging && matchesTransport
  })
})
const selectedNode = computed(
  () => filteredNodes.value.find((node) => node.id === selectedNodeId.value) ?? filteredNodes.value[0]
)
const availableCount = computed(() => dashboard.nodes.filter((node) => node.status === "available").length)
const drainingCount = computed(() => dashboard.nodes.filter((node) => node.status === "draining").length)
const staleCount = computed(() => dashboard.nodes.filter((node) => node.status === "stale").length)
const offlineCount = computed(() => dashboard.nodes.filter((node) => node.status === "offline").length)
const clearOfflineDisabled = computed(
  () =>
    dashboard.dataSource === "fixtures" ||
    dashboard.refreshInFlight ||
    clearOfflineRunning.value ||
    offlineCount.value === 0
)
const clearOfflineTooltip = computed(() => {
  if (dashboard.dataSource === "fixtures") {
    return "Live API only."
  }
  if (offlineCount.value === 0) {
    return "No offline nodes to clear."
  }
  return `Delete ${offlineCount.value} offline node${offlineCount.value === 1 ? "" : "s"} from KV and D1.`
})
const filteredNodeCountLabel = computed(() => {
  const total = dashboard.nodes.length
  const width = Math.max(2, String(total).length)
  return `${String(filteredNodes.value.length).padStart(width, "0")} / ${String(total).padStart(width, "0")}`
})
const filtersDirty = computed(() =>
  Object.entries(defaultFilters).some(([key, value]) => filters[key as keyof NodeFilters] !== value)
)
const nodeColumns: TableColumn<ConnectedNode>[] = [
  {
    accessorKey: "deviceId",
    header: "Device",
    meta: {
      class: {
        td: "min-w-[170px]"
      }
    }
  },
  {
    accessorKey: "status",
    header: "Status"
  },
  {
    accessorKey: "observedIpAddress",
    header: "IP"
  },
  {
    accessorKey: "country",
    header: "Country",
    meta: {
      class: {
        th: "text-center",
        td: "text-center"
      }
    }
  },
  {
    accessorKey: "city",
    header: "City"
  },
  {
    accessorKey: "nodeCategory",
    header: "Category"
  },
  {
    accessorKey: "transport",
    header: "Transport"
  },
  {
    accessorKey: "activeClients",
    header: "Connections",
    meta: {
      class: {
        th: "text-center",
        td: "text-center"
      }
    }
  },
  {
    accessorKey: "lastSeenAt",
    header: "Last seen"
  }
]
const nodeTableMeta = computed(() => ({
  class: {
    tr: (row: TableRow<ConnectedNode>) =>
      row.original.id === selectedNode.value?.id ? "dashboard-table-row--selected cursor-pointer" : "cursor-pointer"
  }
}))

function selectNode(node: ConnectedNode) {
  selectedNodeId.value = node.id
}

function selectNodeRow(_event: Event, row: TableRow<ConnectedNode>) {
  selectNode(row.original)
}

function resetFilters() {
  Object.assign(filters, defaultFilters)
}

async function clearOfflineNodes() {
  if (clearOfflineDisabled.value) {
    return
  }

  clearOfflineRunning.value = true
  clearOfflineMessage.value = ""
  clearOfflineError.value = false

  try {
    const result = await useDashboardApi().runMaintenanceAction("clear-offline-nodes", auth.dashboardToken)
    const deletedCount = result.deletedNodeIds?.length ?? 0
    clearOfflineMessage.value = `Cleared ${formatNumber(deletedCount)} offline node${deletedCount === 1 ? "" : "s"}.`
    await dashboard.refreshFromApi(auth.dashboardToken, { force: true, silent: true })
  } catch (error) {
    clearOfflineError.value = true
    clearOfflineMessage.value = error instanceof Error ? error.message : "Offline node cleanup failed."
  } finally {
    clearOfflineRunning.value = false
  }
}

function nodeLocationLabel(node: ConnectedNode): string {
  if (node.city && node.country) {
    return `${node.country} / ${node.city}`
  }
  return node.country ?? node.city ?? "location unavailable"
}

function nodeCategoryIcon(category: NodeCategory): Component {
  return nodeCategoryIcons[category]
}
</script>

<template>
  <DashboardLayout>
    <div class="dashboard-data-page flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Connected nodes"
        description="Node inventory, liveness, geolocation, and transport."
        details="Filters cover status, category, observed country, charging state, and relay transport for broker-assignment readiness."
      >
        <template #actions>
          <span
            v-if="clearOfflineMessage"
            class="dashboard-mono text-xs"
            :class="clearOfflineError ? 'text-(--dash-danger)' : 'text-(--dash-muted)'"
          >
            {{ clearOfflineMessage }}
          </span>
          <UTooltip
            :text="clearOfflineTooltip"
            :ui="{ content: 'dashboard-tooltip-content', text: 'dashboard-tooltip-text' }"
          >
            <UButton
              color="neutral"
              variant="ghost"
              size="sm"
              :disabled="clearOfflineDisabled"
              :icon="PhTrash"
              :loading="clearOfflineRunning"
              class="dashboard-button dashboard-button--danger vhs-glitch-trigger"
              @click="clearOfflineNodes"
            >
              <span class="vhs-glitch-text" data-vhs-text="CLEAR OFFLINE">Clear Offline</span>
            </UButton>
          </UTooltip>
        </template>
      </PageHeader>

      <DataState
        :loading="dashboard.isLoading"
        :error="dashboard.errorMessage"
        :empty="dashboard.nodes.length === 0"
        empty-centered
        empty-title="No connected nodes"
        empty-description="No node registrations were returned by the Worker dashboard API."
      >
        <section class="dashboard-data-content flex min-w-0 flex-1 flex-col gap-4">
          <section class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <MetricTile
              label="Available"
              :value="formatNumber(availableCount)"
              :tone="availableCount > 0 ? 'success' : 'neutral'"
              :muted="availableCount === 0"
              caption="Ready for broker assignment"
            />
            <MetricTile
              label="Draining"
              :value="formatNumber(drainingCount)"
              :tone="drainingCount > 0 ? 'warning' : 'neutral'"
              :muted="drainingCount === 0"
              caption="Finishing existing client work"
            />
            <MetricTile
              label="Stale"
              :value="formatNumber(staleCount)"
              :tone="staleCount > 0 ? 'warning' : 'neutral'"
              :muted="staleCount === 0"
              caption="Outside heartbeat window"
            />
            <MetricTile
              label="Disconnected"
              :value="formatNumber(offlineCount)"
              :tone="offlineCount > 0 ? 'error' : 'neutral'"
              :muted="offlineCount === 0"
              caption="Expired or explicitly disconnected"
            />
          </section>

          <FilterBar :count-label="filteredNodeCountLabel" :dirty="filtersDirty" @reset="resetFilters">
            <template #search>
              <FilterSearch
                v-model="filters.search"
                data-testid="node-search"
                label="Search nodes"
                placeholder="Search node, IP, city, country, relay"
              />
            </template>
            <template #filters>
              <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
                <FilterSelect v-model="filters.status" :items="statusItems" label="Node status" />
                <FilterSelect v-model="filters.category" :items="categoryItems" label="Node category" />
                <FilterSelect v-model="filters.country" :items="countryItems" label="Node country" />
                <FilterSelect v-model="filters.charging" :items="chargingItems" label="Charging state" />
                <FilterSelect v-model="filters.transport" :items="transportItems" label="Node transport" />
              </div>
            </template>
          </FilterBar>

          <div class="dashboard-data-workspace grid min-w-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            <div class="dashboard-data-table-shell min-w-0">
              <SortableTable
                v-model:sorting="nodeSorting"
                :columns="nodeColumns"
                :data="filteredNodes"
                :meta="nodeTableMeta"
                title="Node inventory"
                eyebrow="Registration state"
                empty-title="No matching nodes"
                empty-description="Relax the filters to show more node registrations."
                modal-description="Node inventory, liveness, observed geography, and broker-assignment readiness."
                :ui="{ root: 'dashboard-table min-w-[960px]', th: 'whitespace-nowrap', td: 'whitespace-nowrap' }"
                sticky="header"
                @select="selectNodeRow"
              >
                <template #deviceId-cell="{ row }">
                  <div class="dashboard-node-device-cell">
                    <component
                      :is="nodeCategoryIcon(row.original.nodeCategory)"
                      :size="15"
                      aria-hidden="true"
                      class="dashboard-node-category-icon"
                    />
                    <template v-if="row.original.deviceId">
                      <ClippedValue
                        :title="row.original.deviceId === row.original.id ? 'Node and Device ID' : 'Device ID'"
                        :value="row.original.deviceId"
                      />
                      <CopyButton :value="row.original.deviceId" label="Copy Device ID" compact class="shrink-0" />
                    </template>
                    <NullBadge v-else field="device" />
                  </div>
                </template>
                <template #status-cell="{ row }">
                  <StatusBadge :status="row.original.status" />
                </template>
                <template #observedIpAddress-cell="{ row }">
                  <span v-if="row.original.observedIpAddress" class="dashboard-mono text-(--dash-ink)">
                    {{ row.original.observedIpAddress }}
                  </span>
                  <NullBadge v-else field="IP" />
                </template>
                <template #country-cell="{ row }">
                  <div class="flex justify-center">
                    <CountryFlag v-if="row.original.country" :country="row.original.country" />
                    <NullBadge v-else field="country" />
                  </div>
                </template>
                <template #city-cell="{ row }">
                  <span v-if="row.original.city">{{ row.original.city }}</span>
                  <NullBadge v-else field="city" />
                </template>
                <template #nodeCategory-cell="{ row }">
                  <StatusBadge :status="row.original.nodeCategory" />
                </template>
                <template #transport-cell="{ row }">
                  <span v-if="row.original.transport">{{ row.original.transport }}</span>
                  <NullBadge v-else />
                </template>
                <template #activeClients-cell="{ row }">
                  <div class="flex justify-center">
                    <CapacityMeter :active="row.original.activeClients" :max="row.original.maxClients" />
                  </div>
                </template>
                <template #lastSeenAt-cell="{ row }">
                  <div class="flex items-center gap-2">
                    <span>{{ formatRelative(row.original.lastSeenAt) }}</span>
                    <PhBatteryCharging
                      v-if="row.original.charging"
                      :size="16"
                      aria-label="Charging"
                      class="text-(--dash-success)"
                    />
                  </div>
                </template>
              </SortableTable>
            </div>

            <Transition name="panel-fade" mode="out-in">
              <DetailPanel
                v-if="selectedNode"
                :key="selectedNode.id"
                :status="selectedNode.status"
                :subtitle="nodeLocationLabel(selectedNode)"
                :title="selectedNode.id"
              >
                <div class="dashboard-node-detail-list grid text-sm">
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Observed IP</span>
                    <span v-if="selectedNode.observedIpAddress" class="dashboard-mono text-right text-(--dash-ink)">
                      {{ selectedNode.observedIpAddress }}
                    </span>
                    <NullBadge v-else field="IP" />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Country</span>
                    <span v-if="selectedNode.country" class="dashboard-mono text-(--dash-ink)">
                      {{ selectedNode.country }}
                    </span>
                    <NullBadge v-else field="country" />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Region</span>
                    <span v-if="selectedNode.regionCode" class="dashboard-mono text-(--dash-ink)">
                      {{ selectedNode.regionCode }}
                    </span>
                    <NullBadge v-else />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Timezone</span>
                    <span v-if="selectedNode.timezone" class="dashboard-mono text-(--dash-ink)">
                      {{ selectedNode.timezone }}
                    </span>
                    <NullBadge v-else />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">ASN</span>
                    <span v-if="selectedNode.asn" class="dashboard-mono text-(--dash-ink)">
                      {{ selectedNode.asn }}
                    </span>
                    <NullBadge v-else />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">ISP</span>
                    <span v-if="selectedNode.isp" class="text-right text-(--dash-ink)">
                      {{ selectedNode.isp }}
                    </span>
                    <NullBadge v-else />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Category</span>
                    <span class="text-(--dash-ink)">{{ selectedNode.nodeCategory }}</span>
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Connections</span>
                    <CapacityMeter :active="selectedNode.activeClients" :max="selectedNode.maxClients" />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Capacity state</span>
                    <span class="dashboard-mono text-(--dash-ink)">
                      {{ selectedNode.acceptingClients ? "accepting" : selectedNode.capacityReason || "held" }}
                    </span>
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Transport</span>
                    <span v-if="selectedNode.transport" class="text-(--dash-ink)">
                      {{ selectedNode.transport }}
                    </span>
                    <NullBadge v-else />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Throughput</span>
                    <span v-if="selectedNode.throughputMbps !== null" class="dashboard-mono text-(--dash-ink)">
                      {{ formatNumber(selectedNode.throughputMbps) }} Mbps
                    </span>
                    <NullBadge v-else />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Last seen</span>
                    <span class="text-(--dash-ink)">{{ formatRelative(selectedNode.lastSeenAt) }}</span>
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">SDK</span>
                    <span v-if="selectedNode.sdkVersion" class="dashboard-mono text-(--dash-ink)">
                      {{ selectedNode.sdkVersion }}
                    </span>
                    <NullBadge v-else />
                  </div>
                  <div class="dashboard-node-detail-row flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">IP family</span>
                    <span v-if="selectedNode.ipFamily" class="text-(--dash-ink)">{{ selectedNode.ipFamily }}</span>
                    <NullBadge v-else />
                  </div>
                </div>

                <template #footer>
                  <div class="flex items-start gap-2 text-xs leading-5 text-(--dash-muted)">
                    <PhAndroidLogo :size="16" class="mt-0.5 shrink-0" />
                    <span>Android nodes use native Android TCP behavior and stay charging-gated.</span>
                  </div>
                </template>
              </DetailPanel>
            </Transition>
          </div>
        </section>
      </DataState>
    </div>
  </DashboardLayout>
</template>
